import { useQuery, useQueryClient } from '@tanstack/react-query';
import { photoApi } from '@/services/api';
import type { ProgressPhoto } from '@/types';

export interface UsePhotosOptions {
  limit?: number;
  startDate?: string;
  endDate?: string;
  autoFetch?: boolean;
}

// Helper function to convert snake_case to camelCase
function parsePhotoFromAPI(photo: any): ProgressPhoto {
  return {
    id: photo.id,
    userId: photo.user_id,
    fileKey: photo.file_key,
    originalFilename: photo.original_filename,
    fileSize: photo.file_size,
    mimeType: photo.mime_type,
    width: photo.width,
    height: photo.height,
    uploadDate: photo.upload_date,
    photoDate: photo.photo_date,
    notes: photo.notes,
    thumbnailKey: photo.thumbnail_key,
    createdAt: photo.created_at,
    updatedAt: photo.updated_at,
    url: photo.url,
    thumbnailUrl: photo.thumbnailUrl
  };
}

/**
 * Hook for managing photo data fetching with TanStack Query
 */
export function usePhotos(options: UsePhotosOptions = {}) {
  const { limit = 20, startDate, endDate, autoFetch = true } = options;
  const queryClient = useQueryClient();

  // Create query key
  const queryKey = ['photos', { limit, startDate, endDate }];

  // Use TanStack Query for data fetching
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await photoApi.getPhotos({
        limit,
        startDate,
        endDate,
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch photos');
      }

      // Parse snake_case to camelCase
      const parsedPhotos = response.data.photos.map(parsePhotoFromAPI);

      return {
        photos: parsedPhotos,
        hasMore: response.data.hasMore || false,
        totalCount: response.data.totalCount || 0,
        nextCursor: response.data.nextCursor
      };
    },
    enabled: autoFetch,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  // Refresh photos (invalidate and refetch)
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey });
  };

  // Add a new photo to the cache (optimistic update)
  const addPhoto = (photo: ProgressPhoto) => {
    queryClient.setQueryData(queryKey, (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        photos: [photo, ...oldData.photos],
        totalCount: oldData.totalCount + 1,
      };
    });
  };

  // Remove a photo from the cache
  const removePhoto = (photoId: string) => {
    queryClient.setQueryData(queryKey, (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        photos: oldData.photos.filter((photo: ProgressPhoto) => photo.id !== photoId),
        totalCount: Math.max(0, oldData.totalCount - 1),
      };
    });
  };

  // Update a photo in the cache
  const updatePhoto = (photoId: string, updates: Partial<ProgressPhoto>) => {
    queryClient.setQueryData(queryKey, (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        photos: oldData.photos.map((photo: ProgressPhoto) =>
          photo.id === photoId ? { ...photo, ...updates } : photo
        ),
      };
    });
  };

  return {
    photos: data?.photos || [],
    loading: isLoading,
    error: error?.message || null,
    hasMore: data?.hasMore || false,
    totalCount: data?.totalCount || 0,
    nextCursor: data?.nextCursor,
    refresh,
    addPhoto,
    removePhoto,
    updatePhoto,
    // For backward compatibility
    fetchPhotos: refetch,
    loadMore: () => { }, // TODO: Implement infinite query for pagination
  };
}